import React, { useEffect, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "@helpers/translate";
import { ReactComponent as WalletIcon } from "@assets/images/icons/wallet-outline.svg";
import { getPairMeta, getPairTicker } from "../Quotes/pairMeta";
import { LEVERAGE_STEPS } from "../Controls/TradeForm/constants";
import { getOpenFeeRate } from "../Controls/TradeForm/utils/fee";
import { formatUsd } from "../Controls/TradeForm/utils/format";
import { nearestStepIndex } from "../Controls/TradeForm/utils/leverage";
import { formatSignedPnl, getTone } from "../Orders/positionFormat";
import { useAssetSettings } from "./assetSettings/useAssetSettings";
import { TradeModal } from "./TradeModal";
import { ModalCloseButton } from "./components/ModalCloseButton";
import { RangeSlider } from "./components/RangeSlider";
import {
  formatCompactMoney,
  formatPriceMove,
  formatWagerStep,
  formatWholeMoney,
} from "./utils/format";
import { getRoiResult } from "./utils/roi";
import "./FeesModal.sass";

const TAB_OPEN_FEE = "open";
const TAB_ROI_FEE = "roi";

// Wager slider steps of Moon (docs/missing-logic.md 3)
const WAGER_STEPS = [
  1, 3, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000,
  100000, 250000, 500000, 1000000, 2500000, 5000000, 10000000,
];
const PRICE_MOVE_DEFAULT = 1;
const PRICE_MOVE_LIMIT = 10;
const PRICE_MOVE_STEP = 0.01;

// Wager steps with the trade form's wager among them, so it opens unchanged
const getWagerSteps = (wager) =>
  wager > 0 && !WAGER_STEPS.includes(wager)
    ? [...WAGER_STEPS, wager].sort((a, b) => a - b)
    : WAGER_STEPS;

const SliderRow = ({ label, value, valueTone, children }) => (
  <div className="fees-modal__slider-row">
    <span className="fees-modal__slider-label">{label}</span>
    {children}
    <span
      className={classnames("fees-modal__slider-value", {
        [`fees-modal__tone--${valueTone}`]: valueTone,
      })}
    >
      {value}
    </span>
  </div>
);

/**
 * "Fees": Open Fee and ROI Fee calculators (docs/trade-logic.md 2.4).
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number} props.wager wager of the trade form
 * @param {number} props.leverage leverage of the trade form
 * @param {object} [props.pair] current pair, for "Max Profit for"
 */
export const FeesModal = ({ isOpen, onClose, wager, leverage, pair }) => {
  const { __ } = useTranslation();
  const [tab, setTab] = useState(TAB_OPEN_FEE);
  const [wagerSteps, setWagerSteps] = useState(WAGER_STEPS);
  const [wagerIndex, setWagerIndex] = useState(0);
  const [leverageIndex, setLeverageIndex] = useState(0);
  const [priceMove, setPriceMove] = useState(PRICE_MOVE_DEFAULT);
  const { settings } = useAssetSettings(pair?.slug);

  // Starts from the trade form values every time it opens
  useEffect(() => {
    if (!isOpen) return;

    const formWager = Number(wager) || 0;
    const steps = getWagerSteps(formWager);

    setTab(TAB_OPEN_FEE);
    setWagerSteps(steps);
    setWagerIndex(Math.max(0, steps.indexOf(formWager)));
    setLeverageIndex(nearestStepIndex(leverage));
    setPriceMove(PRICE_MOVE_DEFAULT);
  }, [isOpen]);

  const wagerValue = wagerSteps[wagerIndex] ?? 0;
  const leverageValue = LEVERAGE_STEPS[leverageIndex];
  const isRoi = tab === TAB_ROI_FEE;
  const pairMeta = getPairMeta(pair?.slug);
  const baseAsset = getPairTicker(pair?.slug);
  // Moon counts the ROI as long whatever the form's direction is
  const { pl, multiplier } = getRoiResult({
    wager: wagerValue,
    leverage: leverageValue,
    priceMovePercent: priceMove,
    riskSettings: settings?.riskSettings,
    maxProfit: settings?.maxProfit,
  });
  const openFee = pair?.open_fee_percent
    ? formatUsd(wagerValue * getOpenFeeRate(pair))
    : "—";
  const plTone = getTone(pl);
  // The summary values are colored by the P&L only on the ROI tab
  const roiToneClass =
    isRoi && plTone ? `fees-modal__tone--${plTone}` : null;

  return (
    <TradeModal
      isOpen={isOpen}
      onClose={onClose}
      className="fees-modal"
      labelledBy="fees-modal-title"
    >
      <header className="fees-modal__header">
        <h3 id="fees-modal-title" className="fees-modal__title">
          <WalletIcon className="fees-modal__title-icon" />
          {__("futures.fees")}
        </h3>
        <ModalCloseButton className="fees-modal__close" onClick={onClose} />
      </header>

      <div className="fees-modal__tabs" role="tablist">
        {[
          [TAB_OPEN_FEE, __("futures.fees_modal.open_fee")],
          [TAB_ROI_FEE, __("futures.fees_modal.roi_fee")],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={classnames("fees-modal__tab", {
              "fees-modal__tab--active": tab === id,
            })}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <p className="fees-modal__description">
        {isRoi
          ? __("futures.fees_modal.roi_description")
          : __("futures.fees_modal.open_description")}
      </p>

      <div className="fees-modal__summary">
        <div className="fees-modal__summary-item">
          <span className="fees-modal__summary-label">
            {isRoi
              ? __("futures.fees_modal.profit_loss")
              : __("futures.fees_modal.fee")}
          </span>
          <span
            className={classnames("fees-modal__summary-value", roiToneClass)}
          >
            {isRoi ? formatSignedPnl(pl) : openFee}
          </span>
        </div>

        <div className="fees-modal__summary-item fees-modal__summary-item--end">
          <span className="fees-modal__summary-label">
            {isRoi
              ? __("futures.fees_modal.multi")
              : __("futures.fees_modal.wager_size")}
          </span>
          <span
            className={classnames(
              "fees-modal__summary-value",
              { "fees-modal__summary-value--small": isRoi },
              roiToneClass,
            )}
          >
            {isRoi
              ? `${multiplier.toFixed(2)}x`
              : formatCompactMoney(wagerValue * leverageValue)}
          </span>
        </div>
      </div>

      <div className="fees-modal__sliders">
        <SliderRow
          label={__("futures.mobile_trade.wager")}
          value={formatWagerStep(wagerValue)}
        >
          <RangeSlider
            value={wagerIndex}
            min={0}
            max={wagerSteps.length - 1}
            onChange={setWagerIndex}
            label={__("futures.mobile_trade.wager")}
          />
        </SliderRow>

        <SliderRow label={__("futures.leverage")} value={`${leverageValue}x`}>
          <RangeSlider
            value={leverageIndex}
            min={0}
            max={LEVERAGE_STEPS.length - 1}
            onChange={setLeverageIndex}
            label={__("futures.leverage")}
          />
        </SliderRow>

        {isRoi && (
          <SliderRow
            label={__("futures.fees_modal.price_move")}
            value={formatPriceMove(priceMove)}
            valueTone={priceMove < 0 ? "down" : "up"}
          >
            <RangeSlider
              value={priceMove}
              min={-PRICE_MOVE_LIMIT}
              max={PRICE_MOVE_LIMIT}
              step={PRICE_MOVE_STEP}
              onChange={setPriceMove}
              label={__("futures.fees_modal.price_move")}
              fillFrom="center"
              tone={priceMove < 0 ? "down" : "up"}
            />
          </SliderRow>
        )}
      </div>

      <div className="fees-modal__max-profit">
        <span className="fees-modal__max-profit-label">
          {__("futures.fees_modal.max_profit_for")}
          <span className="fees-modal__asset">
            {pairMeta?.icon && (
              <img
                src={pairMeta.icon}
                alt=""
                className="fees-modal__asset-icon"
              />
            )}
            {baseAsset}
          </span>
        </span>
        <span className="fees-modal__max-profit-value">
          {settings ? formatWholeMoney(settings.maxProfit) : "—"}
        </span>
      </div>

      <div className="fees-modal__simple">
        <p className="fees-modal__simple-title">
          {__("futures.fees_modal.simple_title")}
        </p>
        <p className="fees-modal__simple-text">
          {isRoi
            ? __("futures.fees_modal.roi_simple")
            : __("futures.fees_modal.open_simple")}
        </p>
      </div>

      <button type="button" className="fees-modal__submit" onClick={onClose}>
        {__("futures.fees_modal.got_it")}
      </button>
    </TradeModal>
  );
};
